<template>
  <div class="record-page">
    <header class="record-header">
      <button class="back-btn" @click="$router.push('/dashboard')">
        <font-awesome-icon icon="arrow-left" />
      </button>
      <h1>Record Temperature</h1>
    </header>

    <!-- Camera / Image Capture -->
    <div class="camera-section">
      <div v-if="!capturedImageUrl" class="camera-prompt">
        <p>Take a photo of the probe or thermometer display, or enter the temperature manually below.</p>
        <label class="camera-btn">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            @change="handleCapture"
            style="display:none"
          />
          <font-awesome-icon icon="camera" />
          Take Photo
        </label>
      </div>

      <div v-else class="image-preview-wrap">
        <img :src="capturedImageUrl" class="captured-img" alt="Captured reading" />
        <div class="ocr-overlay" v-if="ocrProcessing">
          <span>Reading display...</span>
        </div>
        <div class="ocr-result" v-if="ocrExtracted !== null && !ocrProcessing">
          <span>Detected: <strong>{{ ocrExtracted }}°C</strong></span>
          <button type="button" class="use-btn" @click="useOcrValue">Use this</button>
        </div>
        <button type="button" class="retake-btn" @click="retake">
          <font-awesome-icon icon="rotate-right" /> Retake
        </button>
      </div>
    </div>

    <!-- Temperature Form -->
    <form class="temp-form" @submit.prevent="save">
      <div class="form-group">
        <label for="probe-type">Probe / Location</label>
        <select id="probe-type" v-model="form.probeType" required>
          <option value="" disabled>Select probe type...</option>
          <option v-for="probe in probeTypes" :key="probe.id" :value="probe.id">
            {{ probe.label }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="temperature">Temperature (°C)</label>
        <input
          id="temperature"
          type="number"
          step="0.1"
          v-model.number="form.temperature"
          placeholder="e.g. 3.2"
          required
          inputmode="decimal"
          class="temp-input"
        />
      </div>

      <!-- Pass/Fail indicator -->
      <div
        v-if="form.probeType && form.temperature !== '' && !isNaN(form.temperature)"
        :class="['status-indicator', currentProbe && currentProbe.min !== null ? (isInRange ? 'indicator-pass' : 'indicator-fail') : 'indicator-info']"
      >
        <template v-if="currentProbe && currentProbe.min !== null">
          <font-awesome-icon :icon="isInRange ? 'check' : 'triangle-exclamation'" />
          <span v-if="isInRange">PASS — within safe range ({{ currentProbe.min }}°C to {{ currentProbe.max }}°C)</span>
          <span v-else>FAIL — safe range is {{ currentProbe.min }}°C to {{ currentProbe.max }}°C</span>
        </template>
        <template v-else>
          <font-awesome-icon icon="thermometer-half" />
          <span>Ambient reading recorded</span>
        </template>
      </div>

      <!-- Corrective action required if out of range -->
      <div class="form-group" v-if="requiresCorrectiveAction">
        <label for="corrective-action">
          Corrective Action <span class="required">*required</span>
        </label>
        <textarea
          id="corrective-action"
          v-model="form.correctiveAction"
          rows="3"
          placeholder="Describe what action was taken (e.g. removed stock, adjusted thermostat, called engineer)..."
          required
        />
      </div>

      <div class="form-group">
        <label for="notes">Notes (optional)</label>
        <textarea
          id="notes"
          v-model="form.notes"
          rows="2"
          placeholder="Any additional notes..."
        />
      </div>

      <div class="form-group">
        <label for="checked-by">Checked by</label>
        <input
          id="checked-by"
          type="text"
          v-model="form.checkedBy"
          :placeholder="defaultCheckedBy"
        />
      </div>

      <button
        type="submit"
        :disabled="saving || !canSubmit"
        class="save-btn"
      >
        <font-awesome-icon icon="floppy-disk" />
        {{ saving ? 'Saving...' : 'Save Reading' }}
      </button>

      <div class="save-error" v-if="saveError">{{ saveError }}</div>
    </form>
  </div>
</template>

<script>
import { createWorker } from 'tesseract.js';
import { api } from "@monolith/helpers";
import * as AI from "@/helpers/anthropic.js";
const PROBE_TYPES = [
  { id: 'chiller', label: 'Chiller', min: 0, max: 5 },
  { id: 'freezer', label: 'Freezer', min: -30, max: -18 },
  { id: 'hot_hold', label: 'Hot Hold', min: 60, max: 100 },
  { id: 'food_probe', label: 'Food Probe', min: 75, max: 100 },
  { id: 'ambient', label: 'Ambient', min: null, max: null },
];

export default {
  name: "TemperatureRecord",
  data() {
    return {
      probeTypes: PROBE_TYPES,
      capturedImageUrl: null,
      capturedImageBlob: null,
      ocrProcessing: false,
      ocrExtracted: null,
      form: {
        probeType: '',
        temperature: '',
        correctiveAction: '',
        notes: '',
        checkedBy: '',
      },
      saving: false,
      saveError: null,
    }
  },
  computed: {
    currentProbe() {
      return PROBE_TYPES.find(p => p.id === this.form.probeType) || null;
    },
    isInRange() {
      if (!this.currentProbe || this.currentProbe.min === null) return true;
      const t = parseFloat(this.form.temperature);
      return t >= this.currentProbe.min && t <= this.currentProbe.max;
    },
    requiresCorrectiveAction() {
      return (
        this.form.probeType &&
        this.form.temperature !== '' &&
        !isNaN(this.form.temperature) &&
        this.currentProbe &&
        this.currentProbe.min !== null &&
        !this.isInRange
      );
    },
    canSubmit() {
      if (!this.form.probeType || this.form.temperature === '' || isNaN(this.form.temperature)) return false;
      if (this.requiresCorrectiveAction && !this.form.correctiveAction.trim()) return false;
      return true;
    },
    defaultCheckedBy() {
      return localStorage.getItem("station") || '';
    }
  },
  methods: {
    async handleCapture(event) {
      const file = event.target.files[0];
      if (!file) return;
      const { base64, mimeType } = await AI.loadImageAsBase64(file); // file from input/drop
      this.ocrExtracted = null;
      this.ocrProcessing = true;
      this.capturedImageBlob = file;
      this.capturedImageUrl = URL.createObjectURL(file);
      const result = await AI.readTemperature(base64, mimeType);
      //await this.runOcr(file);
      console.log(result);
      this.ocrExtracted = parseFloat(result);
      this.ocrProcessing = false;
    },
    async runOcr(imageFile) {
      this.ocrProcessing = true;
      try {
        const worker = await createWorker('eng');
        await worker.setParameters({
          tessedit_char_whitelist: '0123456789.-',
        });
        const { data: { text } } = await worker.recognize(imageFile);
        await worker.terminate();

        // Extract first valid number from OCR text
        const match = text.match(/-?\d+(\.\d+)?/);
        if (match) {
          this.ocrExtracted = parseFloat(match[0]);
        }
      } catch {
        // OCR failed — user will enter manually
        this.ocrExtracted = null;
      } finally {
        this.ocrProcessing = false;
      }
    },
    useOcrValue() {
      this.form.temperature = this.ocrExtracted;
    },
    retake() {
      this.capturedImageUrl = null;
      this.capturedImageBlob = null;
      this.ocrExtracted = null;
      this.ocrProcessing = false;
    },
    async save() {
      if (!this.canSubmit) return;
      this.saving = true;
      this.saveError = null;

      const reading = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        probeType: this.form.probeType,
        temperature: parseFloat(this.form.temperature),
        pass: this.currentProbe && this.currentProbe.min !== null ? this.isInRange : true,
        correctiveAction: this.form.correctiveAction || null,
        notes: this.form.notes || null,
        checkedBy: this.form.checkedBy || this.defaultCheckedBy,
				hasPhoto: !!this.capturedImageBlob,
				site: await localStorage.getItem('site')
      };

      try {
        await api.post(`/api/temperature`, reading);
        this.$store.dispatch('addReading', reading);
        this.$router.push('/dashboard');
      } catch (e) {
        // Save locally if API unavailable
        this.$store.dispatch('addReading', reading);
        this.saveError = "Saved locally — could not sync to server. Will retry on next load.";
        setTimeout(() => this.$router.push('/dashboard'), 1500);
      } finally {
        this.saving = false;
      }
    }
  }
}
</script>

<style scoped lang="scss">
.record-page {
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 2rem;
}

.record-header {
  background-color: var(--banner-colour);
  color: #fff;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  h1 {
    margin: 0;
    font-size: 1.1rem;
  }
}

.back-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.1rem;
  padding: 0.25rem 0.5rem;
  margin-left: 0;
  cursor: pointer;
}

.camera-section {
  margin: 1.25rem 1.25rem 0;
}

.camera-prompt {
  background-color: var(--secondary-colour);
  border-radius: 0.5rem;
  padding: 1.25rem;
  text-align: center;

  p {
    margin: 0 0 1rem;
    font-size: 0.9rem;
    opacity: 0.8;
  }
}

.camera-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--action-colour);
  color: #fff;
  padding: 0.9rem 1.5rem;
  border-radius: 0.4rem;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
}

.image-preview-wrap {
  position: relative;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #000;
}

.captured-img {
  width: 100%;
  display: block;
  max-height: 300px;
  object-fit: contain;
}

.ocr-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: bold;
}

.ocr-result {
  position: absolute;
  bottom: 2.5rem;
  left: 0;
  right: 0;
  background: rgba(0,0,0,0.75);
  color: #fff;
  padding: 0.6rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.95rem;
}

.use-btn {
  background-color: var(--pass-colour);
  border: none;
  color: #fff;
  padding: 0.35rem 0.75rem;
  border-radius: 0.3rem;
  font-size: 0.85rem;
  margin-left: 0;
  cursor: pointer;
}

.retake-btn {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(0,0,0,0.6);
  border: none;
  color: #fff;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  margin-left: 0;
  cursor: pointer;
  border-radius: 0;
}

.temp-form {
  padding: 1.25rem;
}

.form-group {
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.85rem;
    font-weight: bold;
    margin-bottom: 0.4rem;
  }

  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border-radius: 0.4rem;
    border: 1px solid var(--border-colour);
    font-size: 1rem;
    font-family: "Lato";
    background-color: var(--primary-colour);
    color: var(--text-colour);
  }
}

.temp-input {
  font-size: 2rem !important;
  font-weight: bold;
  text-align: center;
  letter-spacing: 0.05em;
}

.required {
  color: var(--fail-colour);
  font-weight: normal;
  font-size: 0.8em;
}

.status-indicator {
  border-radius: 0.4rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: bold;
  font-size: 0.9rem;

  &.indicator-pass {
    background-color: rgba(76, 175, 80, 0.15);
    color: var(--pass-colour);
    border: 1px solid var(--pass-colour);
  }
  &.indicator-fail {
    background-color: rgba(244, 67, 54, 0.15);
    color: var(--fail-colour);
    border: 1px solid var(--fail-colour);
  }
  &.indicator-info {
    background-color: var(--secondary-colour);
    opacity: 0.8;
  }
}

.save-btn {
  width: 100%;
  margin-left: 0;
  padding: 1rem;
  font-size: 1rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.save-error {
  margin-top: 0.75rem;
  color: var(--warning-colour);
  font-size: 0.85rem;
  text-align: center;
}
</style>
